import { useEffect } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet-draw";
import "leaflet-draw/dist/leaflet.draw.css";

/**
 * DrawSearchControl — adds a Leaflet-Draw toolbar to the map that lets the
 * user draw a polygon, rectangle, or circle. On create, calls `onShape` with
 * a normalized shape descriptor:
 *   { type: "polygon", latlngs: [[lat,lng], ...] }
 *   { type: "circle",  center: [lat,lng], radius: <meters> }
 *   null (when the shape is cleared/deleted)
 *
 * Only one shape at a time; drawing a new one replaces the previous.
 */
export default function DrawSearchControl({ onShape, clearSignal = 0 }) {
  const map = useMap();

  useEffect(() => {
    const drawnItems = new L.FeatureGroup();
    map.addLayer(drawnItems);
    drawnItems.__gensClear = () => drawnItems.clearLayers();
    map.__gensDrawnItems = drawnItems;

    const control = new L.Control.Draw({
      position: "topright",
      edit: { featureGroup: drawnItems, remove: true },
      draw: {
        polygon: {
          allowIntersection: false,
          showArea: false,
          shapeOptions: { color: "#E63946", weight: 3, fillOpacity: 0.15 },
        },
        rectangle: { shapeOptions: { color: "#E63946", weight: 3, fillOpacity: 0.15 } },
        circle: { shapeOptions: { color: "#E63946", weight: 3, fillOpacity: 0.15 } },
        polyline: false,
        marker: false,
        circlemarker: false,
      },
    });
    map.addControl(control);

    const shapeOf = (layer) => {
      if (layer instanceof L.Circle) {
        const c = layer.getLatLng();
        return { type: "circle", center: [c.lat, c.lng], radius: layer.getRadius() };
      }
      // Polygon or rectangle — both expose getLatLngs()[0]
      const ring = layer.getLatLngs()?.[0] || [];
      return {
        type: "polygon",
        latlngs: ring.map((p) => [p.lat, p.lng]),
      };
    };

    const onCreated = (e) => {
      drawnItems.clearLayers(); // enforce single-shape mode
      drawnItems.addLayer(e.layer);
      onShape?.(shapeOf(e.layer));
    };
    const onEdited = (e) => {
      let s = null;
      e.layers.eachLayer((layer) => { s = shapeOf(layer); });
      if (s) onShape?.(s);
    };
    const onDeleted = () => onShape?.(null);

    map.on(L.Draw.Event.CREATED, onCreated);
    map.on(L.Draw.Event.EDITED, onEdited);
    map.on(L.Draw.Event.DELETED, onDeleted);

    return () => {
      map.off(L.Draw.Event.CREATED, onCreated);
      map.off(L.Draw.Event.EDITED, onEdited);
      map.off(L.Draw.Event.DELETED, onDeleted);
      map.removeControl(control);
      map.removeLayer(drawnItems);
      delete map.__gensDrawnItems;
    };
  }, [map, onShape]);

  // External clear trigger — parent bumps clearSignal to erase the drawn layer
  useEffect(() => {
    if (clearSignal && map.__gensDrawnItems) {
      map.__gensDrawnItems.clearLayers();
    }
  }, [clearSignal, map]);

  return null;
}

// Point-in-polygon (ray casting). polygon = array of [lat,lng] pairs.
export function pointInPolygon(lat, lng, polygon) {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [yi, xi] = polygon[i];
    const [yj, xj] = polygon[j];
    const intersect =
      yi > lat !== yj > lat &&
      lng < ((xj - xi) * (lat - yi)) / (yj - yi + 1e-12) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

// Great-circle distance in meters (Haversine).
export function distanceMeters(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

export function pointInShape(lat, lng, shape) {
  if (!shape) return true;
  if (shape.type === "polygon") return pointInPolygon(lat, lng, shape.latlngs);
  if (shape.type === "circle") {
    return distanceMeters(lat, lng, shape.center[0], shape.center[1]) <= shape.radius;
  }
  return true;
}
